import { AlertTriangle, CheckCircle2, Download } from 'lucide-react';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import type {
  CostSummary,
  DeltaResult,
  VerificationCategory,
  VerificationResult,
} from '../../types/cost-accounting.js';

/** @graph-connects none */
function yen(amount: number): string {
  return `¥${amount.toLocaleString('ja-JP')}`;
}

/** @graph-connects none */
function SummaryCard({
  label,
  amount,
  count,
  color,
  bg,
  countLabel,
}: {
  label: string;
  amount: number;
  count: number;
  color: string;
  bg: string;
  countLabel?: string;
}) {
  return (
    <div className="rounded-xl border border-[var(--color-border)] p-4" style={{ background: bg }}>
      <div className="flex items-center gap-1.5 text-xs font-semibold" style={{ color }}>
        <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: color }} />
        {label}
      </div>
      <div className="mt-2.5 text-2xl font-bold tabular-nums" style={{ letterSpacing: '-0.01em' }}>
        {yen(amount)}
      </div>
      <div className="mt-0.5 text-xs tabular-nums text-[var(--color-text-muted)]">
        {countLabel ?? `${count.toLocaleString('ja-JP')} 件`}
      </div>
    </div>
  );
}

/** @graph-connects none */
const CATEGORY_LABELS: Record<VerificationCategory, string> = {
  numerical: '数値整合性',
  'data-quality': 'データ品質',
};

/** @graph-connects none */
function VerificationPanel({
  verification,
  onViewDetail,
}: {
  verification: VerificationResult;
  onViewDetail?: () => void;
}) {
  const borderColor = verification.allOk ? '#16a34a' : '#dc2626';
  const headerColor = verification.allOk ? '#16a34a' : '#dc2626';

  const okCount = verification.items.filter((i) => i.ok).length;
  const totalCount = verification.items.length;

  const categories: VerificationCategory[] = ['numerical', 'data-quality'];

  return (
    <div
      className="mt-4 rounded-xl p-4"
      style={{ background: 'var(--color-bg-base)', border: `1px solid ${borderColor}` }}
    >
      <div className="flex items-center gap-2 text-sm font-bold" style={{ color: headerColor }}>
        {verification.allOk ? (
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
        ) : (
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        )}
        {verification.allOk ? '検算OK' : '検算NG — 不一致あり'}
        <span className="ml-1 text-xs font-normal text-[var(--color-text-muted)]">
          {String(okCount)}/{String(totalCount)}
        </span>
      </div>
      <div className="mt-3 flex flex-col gap-2">
        {categories.map((cat) => {
          const catItems = verification.items.filter((i) => i.category === cat);
          if (catItems.length === 0) {
            return null;
          }
          return (
            <div key={cat}>
              <div className="mt-2 border-t border-[var(--color-border)] pt-2 text-[0.625rem] font-semibold tracking-wider text-[var(--color-text-muted)]">
                {CATEGORY_LABELS[cat]}
              </div>
              {catItems.map((item) => (
                <div
                  key={item.label}
                  className="flex flex-wrap items-baseline gap-3 text-xs text-[var(--color-text-secondary)]"
                >
                  <span className="min-w-[18ch]">{item.label}</span>
                  <span className="font-mono text-[0.6875rem] text-[var(--color-text-muted)]">
                    {item.formula}
                  </span>
                  <span
                    className="ml-auto font-semibold"
                    style={{ color: item.ok ? '#16a34a' : '#dc2626' }}
                  >
                    {item.result} {item.ok ? '✓' : '✗'}
                  </span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
      {onViewDetail && (
        <div className="mt-3 text-right">
          <button
            type="button"
            onClick={onViewDetail}
            className="text-xs font-medium text-[#1d4ed8] hover:underline"
          >
            検算詳細を見る →
          </button>
        </div>
      )}
    </div>
  );
}

/** @graph-connects none */
function DeltaTable({ delta }: { delta: DeltaResult }) {
  return (
    <div className="mt-4">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="py-1.5 pr-3 text-left font-semibold text-[var(--color-text-muted)]">
                指標
              </th>
              <th className="py-1.5 px-3 text-right font-semibold text-[var(--color-text-muted)]">
                前月({delta.prevMonth.slice(5)})
              </th>
              <th className="py-1.5 px-3 text-right font-semibold text-[var(--color-text-muted)]">
                当月({delta.currentMonth.slice(5)})
              </th>
              <th className="py-1.5 px-3 text-right font-semibold text-[var(--color-text-muted)]">
                差分
              </th>
              <th className="py-1.5 px-3 text-right font-semibold text-[var(--color-text-muted)]">
                変動率
              </th>
              <th className="py-1.5 pl-3 text-left font-semibold text-[var(--color-text-muted)]">
                要因
              </th>
            </tr>
          </thead>
          <tbody>
            {delta.entries.map((e) => (
              <tr key={e.metric} className="border-b border-[var(--color-border)]">
                <td className="py-1.5 pr-3 font-medium">{e.metric}</td>
                <td className="py-1.5 px-3 text-right tabular-nums">{yen(e.prevAmount)}</td>
                <td className="py-1.5 px-3 text-right tabular-nums">{yen(e.currentAmount)}</td>
                <td
                  className="py-1.5 px-3 text-right tabular-nums font-semibold"
                  style={{
                    color:
                      e.diff > 0 ? '#dc2626' : e.diff < 0 ? '#16a34a' : 'var(--color-text-muted)',
                  }}
                >
                  {e.diff > 0 ? '+' : ''}
                  {yen(e.diff)}
                </td>
                <td className="py-1.5 px-3 text-right tabular-nums">
                  {e.changeRate !== null ? (
                    <span
                      className="inline-block rounded px-1.5 py-0.5 text-[0.625rem] font-semibold"
                      style={{
                        background:
                          e.diff > 0
                            ? '#fef2f2'
                            : e.diff < 0
                              ? '#f0fdf4'
                              : 'var(--color-bg-subtle)',
                        color:
                          e.diff > 0
                            ? '#dc2626'
                            : e.diff < 0
                              ? '#16a34a'
                              : 'var(--color-text-muted)',
                      }}
                    >
                      {e.changeRate > 0 ? '+' : ''}
                      {String(e.changeRate)}%
                    </span>
                  ) : (
                    <span className="inline-block rounded bg-[var(--color-bg-subtle)] px-1.5 py-0.5 text-[0.625rem] font-semibold text-[var(--color-text-muted)]">
                      —
                    </span>
                  )}
                </td>
                <td className="py-1.5 pl-3 text-[var(--color-text-muted)]">{e.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 rounded-lg bg-[var(--color-bg-subtle)] p-3 text-[0.6875rem] leading-relaxed text-[var(--color-text-secondary)]">
        <strong className="text-[var(--color-text-primary)]">変動要因サマリ:</strong>{' '}
        {delta.summary}
      </div>
    </div>
  );
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business Single-month summary view with 6 metric cards, 8-item verification panel with category grouping, month-over-month delta table, and CSV download button.
 * @graph-connects none
 */
export function CostAccountingSummaryView({
  summary,
  verification,
  delta,
  onSummaryCsvDownload,
  onViewVerificationDetail,
}: {
  summary: CostSummary;
  verification: VerificationResult;
  delta?: DeltaResult;
  onSummaryCsvDownload: () => void;
  onViewVerificationDetail?: () => void;
}) {
  return (
    <>
      <div className="mt-6 grid grid-cols-3 gap-3">
        <SummaryCard
          label="減価償却費"
          amount={summary.depreciation.amount}
          count={summary.depreciation.count}
          color="#475569"
          bg="var(--color-bg-subtle)"
        />
        <SummaryCard
          label="廃棄原価"
          amount={summary.disposal.amount}
          count={summary.disposal.count}
          color="#b45309"
          bg="#fef3c7"
        />
        <SummaryCard
          label="販売原価"
          amount={summary.sale.amount}
          count={summary.sale.count}
          color="#0f766e"
          bg="#ecfdf5"
        />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <SummaryCard
          label="償却残"
          amount={summary.remaining.amount}
          count={summary.remaining.count}
          color="#1d4ed8"
          bg="#eff6ff"
        />
        <SummaryCard
          label="当月計上合計"
          amount={summary.total.amount}
          count={summary.total.count}
          color="var(--color-text-secondary)"
          bg="var(--color-bg-base)"
          countLabel="減価償却費 + 廃棄 + 販売"
        />
      </div>
      <VerificationPanel verification={verification} onViewDetail={onViewVerificationDetail} />
      {delta && <DeltaTable delta={delta} />}
      <div className="mt-3 flex justify-end">
        <Button variant="secondary" onClick={onSummaryCsvDownload}>
          <Download className="mr-1.5 h-4 w-4" aria-hidden="true" />
          サマリCSV
        </Button>
      </div>
    </>
  );
}
